import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { loadStripe } from "@stripe/stripe-js";

import { db, functions } from "../firebase";

const createDropUnlockCheckoutFn = httpsCallable(functions, "createDropUnlockCheckout");
const confirmDropUnlockFn = httpsCallable(functions, "confirmDropUnlock");

function callableError(err) {
  const code = err?.code || "";
  const msg = err?.message || err?.details || "Request failed.";
  if (code === "functions/not-found" || /not-found/i.test(msg)) {
    return "Payment service is not deployed yet. Ask the team to deploy createDropUnlockCheckout.";
  }
  if (code === "functions/unauthenticated") {
    return "Sign in again to unlock this drop.";
  }
  return msg.replace(/^Firebase:\s*/i, "").replace(/\s*\(.*\)\s*$/, "") || msg;
}

export async function createDropUnlockCheckout({
  dropId,
  quickPay = false,
  kind = "unlock",
}) {
  try {
    const { data } = await createDropUnlockCheckoutFn({ dropId, quickPay, kind });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function confirmDropUnlock({ paymentIntentId, dropId }) {
  try {
    const { data } = await confirmDropUnlockFn({ paymentIntentId, dropId });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

/**
 * One-shot cash unlock / archive purchase.
 * Returns { status: 'granted'|'needs_payment'|'requires_action', ...data }
 */
export async function purchaseDropAccess({
  dropId,
  kind = "unlock",
  publishableKeyFallback = "",
}) {
  const data = await createDropUnlockCheckout({ dropId, quickPay: true, kind });

  if (data.alreadyUnlocked || data.quickPaid) {
    return { status: "granted", ...data };
  }

  if (data.requiresAction && data.clientSecret) {
    const key =
      data.publishableKey
      || publishableKeyFallback
      || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
      || "";
    const stripe = await loadStripe(key);
    if (!stripe) throw new Error("Stripe failed to load.");
    const { error, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret);
    if (error) throw new Error(error.message || "Card authentication failed.");
    if (paymentIntent?.status === "succeeded") {
      await confirmDropUnlock({
        paymentIntentId: paymentIntent.id || data.paymentIntentId,
        dropId,
      });
      return { status: "granted", ...data, paymentIntentId: paymentIntent.id };
    }
    throw new Error(`Payment status: ${paymentIntent?.status || "unknown"}`);
  }

  if (data.clientSecret) {
    return { status: "needs_payment", ...data };
  }

  throw new Error("Payment could not be started.");
}

/** True if this user has a cash (or mapped) unlock for the drop. */
export async function checkPaidDropUnlock(userId, dropId) {
  if (!userId || !dropId) return false;
  try {
    const [userSnap, unlockSnap, archiveSnap] = await Promise.all([
      getDoc(doc(db, "users", userId)),
      getDoc(doc(db, "dropUnlocks", `${dropId}_${userId}`)),
      getDoc(doc(db, "archive_purchases", `${dropId}_${userId}`)),
    ]);
    if (unlockSnap.exists()) return true;
    if (archiveSnap.exists()) return true;
    if (userSnap.exists() && userSnap.data()?.unlockedDrops?.[dropId]) return true;
    return false;
  } catch {
    return false;
  }
}
